import { openDB, IDBPDatabase } from 'idb';

// Collect comprehensive error data
export async function logErrorToDB(logerror: Error, additionalInfo: any) {
  try {
    const db = await initDB();
    const errorData: ErrorData = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      message: logerror.message || 'Unknown error',
      stack: logerror.stack || 'No stack trace available',
      url: globalThis.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      additionalInfo,
      user: {
        userId: (globalThis as any).currentUser?.id || 'anonymous',
        email: (globalThis as any).currentUser?.email || null,
        role: (globalThis as any).currentUser?.role || null,
        sessionId: (globalThis as any).currentUser?.sessionId || null,
      },
      device: {
        screenResolution: `${globalThis.screen.width}x${globalThis.screen.height}`,
        viewportSize: `${globalThis.innerWidth}x${globalThis.innerHeight}`,
        language: navigator.language,
        platform: navigator.platform,
        cookiesEnabled: navigator.cookieEnabled,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        connection: (navigator as any).connection
          ? {
              type: (navigator as any).connection.type,
              effectiveType: (navigator as any).connection.effectiveType,
              downlink: (navigator as any).connection.downlink,
              rtt: (navigator as any).connection.rtt,
            }
          : null,
        deviceMemory: (navigator as any).deviceMemory || null,
      },
      appContext: {
        pageTitle: document.title,
        referrer: document.referrer,
        sessionDuration:
          (Date.now() -
            (globalThis.performance.timing?.navigationStart || Date.now())) /
          1000,
        componentStack: additionalInfo.componentStack || null,
        routeHistory: globalThis.history.length,
      },
      performance: {
        loadTime: globalThis.performance.timing?.domContentLoadedEventEnd
          ? (globalThis.performance.timing.domContentLoadedEventEnd -
              globalThis.performance.timing.navigationStart) /
            1000
          : null,
        memory: (globalThis.performance as any).memory
          ? {
              jsHeapSizeLimit: (globalThis.performance as any).memory
                .jsHeapSizeLimit,
              totalJSHeapSize: (globalThis.performance as any).memory
                .totalJSHeapSize,
              usedJSHeapSize: (globalThis.performance as any).memory
                .usedJSHeapSize,
            }
          : null,
        timing: globalThis.performance.timing
          ? {
              navigationStart: globalThis.performance.timing.navigationStart,
              responseEnd: globalThis.performance.timing.responseEnd,
              domInteractive: globalThis.performance.timing.domInteractive,
            }
          : null,
      },
    };
    await db.put(STORE_NAME, errorData);
  } catch (error) {
    console.error('Failed to log error to DB:', error);
  }
}
export async function removedOver5days() {
  try {
    const db = await initDB();
    const errors: ErrorData[] = await db.getAll(STORE_NAME);
    const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;
    const INTERVAL_MS = 60 * 1000; // 1 minute in milliseconds
    const now = Date.now();
    // Find IDs of errors older than 5 days
    const oldErrorIds = errors
      .filter(
        (error) => now - new Date(error.timestamp).getTime() > FIVE_DAYS_MS
      )
      .map((error) => error.id);

    if (oldErrorIds.length === 0) return;

    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    // Delete only old entries
    for (const id of oldErrorIds) {
      await store.delete(id);
    }
    await tx.done;
  } catch (error) {
    console.error('Network error while sending batched errors:', error);
  }
}
// Send batched errors to server
export async function sendBatchedErrors() {
  try {
    const db = await initDB();
    const errors: ErrorData[] = await db.getAll(STORE_NAME);
    if (errors.length === 0) return;

    const response = await fetch('/api/errors/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(errors),
    });

    if (response.ok) {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      await store.clear();
      await tx.done;
    } else {
      console.error('Failed to send batched errors:', response.status);
    }
  } catch (error) {
    console.error('Network error while sending batched errors:', error);
  }
}

// Define types for error data
interface UserInfo {
  userId: string;
  email: string | null;
  role: string | null;
  sessionId: string | null;
}

interface DeviceInfo {
  screenResolution: string;
  viewportSize: string;
  language: string;
  platform: string;
  cookiesEnabled: boolean;
  timezone: string;
  connection: {
    type?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  } | null;
  deviceMemory: number | null;
}

interface AppContext {
  pageTitle: string;
  referrer: string;
  sessionDuration: number;
  componentStack: string | null;
  routeHistory: number;
}

interface PerformanceInfo {
  loadTime: number | null;
  memory: {
    jsHeapSizeLimit: number;
    totalJSHeapSize: number;
    usedJSHeapSize: number;
  } | null;
  timing: {
    navigationStart: number;
    responseEnd: number;
    domInteractive: number;
  } | null;
}

interface ErrorData {
  id: string;
  message: string;
  stack: string;
  url: string;
  userAgent: string;
  timestamp: string;
  additionalInfo: any;
  user: UserInfo;
  device: DeviceInfo;
  appContext: AppContext;
  performance: PerformanceInfo;
}

// Initialize IndexedDB
const DB_NAME = 'ErrorLoggerDB';
const STORE_NAME = 'errors';
async function initDB(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, {
          keyPath: 'id',
          autoIncrement: false,
        });
      }
    },
  });
}
